import { useMemo, useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Brain, ChevronRight, ChevronDown, MessageSquare, Sparkles, User, ArrowRight, Swords,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { proseClasses } from "@/lib/prose-classes";
import { CATEGORY_BADGES } from "@/lib/message-classification";
import { useMessageMeta } from "@/hooks/useMessageMeta";
import type { EntityMedia } from "./NarratorMessage";
import type { MessageCardProps } from "./MessageCard";
import { MessageCardMenu } from "./MessageCardMenu";
import { MessageDebugDialog } from "./MessageDebugDialog";
import { downloadMessageDebugLog } from "@/lib/message-debug";
import { GMAvatar } from "./GMAvatar";

// ── Shared helpers ──

const SOFT_WORD_LIMIT = 200;
function truncateToSoftLimit(text: string): { truncated: string; isTruncated: boolean } {
  const words = text.split(/\s+/);
  if (words.length <= SOFT_WORD_LIMIT) return { truncated: text, isTruncated: false };
  const joined = words.slice(0, SOFT_WORD_LIMIT).join(" ");
  const lastSentence = joined.lastIndexOf(".");
  const cutoff = lastSentence > joined.length * 0.6 ? lastSentence + 1 : joined.length;
  return { truncated: text.slice(0, text.indexOf(joined.slice(cutoff - 1)) + 1) || joined, isTruncated: true };
}

// ── Agent Debug Dialog (same as MessageCard — admin only) ──
function AgentDebugDialog({ metadata }: { metadata: any }) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  // same implementation as MessageCard's AgentDebugDialog — keep inline for simplicity
  const classification = metadata?.intent_classification;
  const directorNotes = metadata?.director_notes;
  const subAgentSummary = metadata?.sub_agent_summary;
  const subActions = metadata?.sub_actions;
  const inputTokens = metadata?.input_tokens;
  const outputTokens = metadata?.output_tokens;
  const creditsUsed = metadata?.credits_used;
  const stateChanges = metadata?.state_changes;
  const mechanicResults = metadata?.mechanic_results;
  const toolsCalled = metadata?.tools_called;
  const agentsUsed = metadata?.agents_used;
  const isInspection = classification?.is_inspection;
  const isPlanning = classification?.is_planning;
  const inspectionTarget = classification?.inspection_target;
  const hasContent = classification || directorNotes || subAgentSummary || subActions?.length;
  if (!hasContent) return null;
  const agentCount = agentsUsed?.length || (subAgentSummary ? 1 : 0);
  const toolCount = toolsCalled || 0;

  return (
    <>
      <Button variant="ghost" size="icon" className="h-5 w-5 rounded-full text-muted-foreground/50 hover:text-primary hover:bg-primary/10" aria-label="View agent debug information" onClick={() => setOpen(true)}>
        <Brain className="w-3 h-3" />
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-background border rounded-lg shadow-xl max-w-lg w-[95vw] max-h-[85vh] overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold"><Brain className="w-4 h-4 text-primary" />Agent Process</div>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen(false)}>✕</Button>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {classification?.primary_intent && <Badge variant="outline" className="text-[10px] h-5">{classification.primary_intent}</Badge>}
              {isInspection && <Badge variant="outline" className="text-[10px] h-5 text-amber-500 border-amber-500/30">Inspect{inspectionTarget ? `: ${inspectionTarget}` : ""}</Badge>}
              {isPlanning && <Badge variant="outline" className="text-[10px] h-5 text-blue-500 border-blue-500/30">Planning</Badge>}
              {agentCount > 0 && <Badge variant="secondary" className="text-[10px] h-5">{agentCount} agents</Badge>}
              {toolCount > 0 && <Badge variant="secondary" className="text-[10px] h-5">{toolCount} tools</Badge>}
            </div>
            <div className="space-y-2 text-xs">
              {classification?.reasoning && <p className="text-muted-foreground italic">{classification.reasoning}</p>}
              {subActions?.length > 0 && <div><span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Sub-Actions</span><div className="space-y-0.5 mt-1">{subActions.map((sa: any, i: number) => (<div key={i} className="flex items-start gap-1.5"><span className="text-primary font-mono text-[10px]">{i + 1}.</span><span className="text-foreground/80">{typeof sa === "string" ? sa : `[${sa.intent}] ${sa.text}`}</span></div>))}</div></div>}
              {directorNotes?.narrative_guidance && <div><span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Director</span><p className="text-violet-400/80 mt-0.5">{directorNotes.narrative_guidance}</p></div>}
              {subAgentSummary && <div><span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Agents</span><pre className="text-[10px] text-foreground/70 mt-0.5 whitespace-pre-wrap font-mono bg-muted/30 rounded p-1.5 max-h-24 overflow-auto">{typeof subAgentSummary === "string" ? subAgentSummary : JSON.stringify(subAgentSummary, null, 2)}</pre></div>}
              {(inputTokens || outputTokens) && <div className="text-muted-foreground">Tokens: {inputTokens ?? "?"} → {outputTokens ?? "?"}</div>}
              {creditsUsed != null && <div className="text-muted-foreground">Credits: {Number(creditsUsed).toFixed(4)}</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Debug section helper ──
function DebugSection({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{icon}{title}</div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Prose view mode — narrator text as flowing paragraphs,
// player actions as compact inline annotations.
// ═══════════════════════════════════════════════════════════

export interface MessageCardProseProps extends Omit<MessageCardProps, "isSceneIntro" | "isLastMessage" | "isDeactivated"> {
  /** Whether the previous filtered message is also a narrator. */
  isPreviousNarrator?: boolean;
  /** Whether the next filtered message is also a narrator. */
  isNextNarrator?: boolean;
}

export function MessageCardProse({
  msg, entityLookup, adventureState, onSpeak, onSpeakMessage, onDownload, onPause, onResume, onStop,
  speakingMsgId, speakingVoiceKey, isPaused, defaultVoiceId, onLocationClick, onSpeakerClick,
  isHidden, onToggleHide, onReclassify, onDelete, onEdit, onRetry, canEdit,
  isAdmin, isWorldOwner, worldId, currentUserId, authorUserId,
  isPreviousNarrator, isNextNarrator,
}: MessageCardProseProps) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const {
    isOOC, isInnerVoice, isGmQuery, isSystemNote, msgCategory, badge,
    classification, directorNotes, subActions, creditsUsed, actionEcho,
    segmentCategories, hasMultipleCategories, isPlayer, isNarrator, isRtl,
  } = useMessageMeta(msg);

  const canDebug = !!isAdmin || !!isWorldOwner;
  const [debugOpen, setDebugOpen] = useState(false);

  const isOwnMessage = !!currentUserId && (
    (authorUserId && authorUserId === currentUserId) || (!authorUserId && isPlayer)
  );
  const canReport = !!currentUserId && !isOwnMessage && !isSystemNote;

  const speakFull = () => onSpeakMessage ? onSpeakMessage(msg.id, msg.content) : onSpeak(msg.id, msg.content, "narrator");
  const downloadFull = () => onDownload?.(msg.id, msg.content, "narrator");
  const isThisMsgPlaying = speakingMsgId === msg.id;

  const { displayContent, showExpand } = useMemo(() => {
    if (isPlayer || isOOC || isInnerVoice || isGmQuery) return { displayContent: msg.content, showExpand: false };
    if (expanded) return { displayContent: msg.content, showExpand: false };
    const { truncated, isTruncated } = truncateToSoftLimit(msg.content);
    return { displayContent: isTruncated ? truncated : msg.content, showExpand: isTruncated };
  }, [msg.content, isPlayer, isOOC, isInnerVoice, isGmQuery, expanded]);

  // ── Player annotation ──
  if (isPlayer) {
    return (
      <div dir={isRtl ? "rtl" : undefined} className={cn("group/msg relative my-4", isHidden && "opacity-40")}>
        {/* Menu overlay — absolute top-right, visible on hover */}
        <div className="absolute top-0 right-0 z-10 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-0.5">
          {canDebug && <AgentDebugDialog metadata={msg.metadata} />}
          <MessageCardMenu
            msgId={msg.id} msgContent={msg.content} msgCategory={msgCategory}
            canTTS={false} isThisMsgPlaying={false}
            onSpeak={() => {}} onDownload={() => {}}
            onToggleHide={onToggleHide} isHidden={isHidden}
            onReclassify={onReclassify} onDelete={onDelete}
            onEdit={canEdit ? () => onEdit?.(msg.id, msg.content) : undefined}
            canEdit={canEdit}
            onRetry={onRetry ? () => onRetry(msg.id) : undefined}
            creditsUsed={creditsUsed}
            canReport={canReport}
            worldId={worldId ?? null}
            onDebugVisualize={canDebug ? () => setDebugOpen(true) : undefined}
            onDebugDownload={canDebug ? () => downloadMessageDebugLog(msg) : undefined}
          />
        </div>
        {canDebug && <MessageDebugDialog msg={msg} open={debugOpen} onOpenChange={setDebugOpen} />}
        <div className={cn(
          "ml-auto max-w-[70%] border-l-[1.5px] pl-3 text-xs text-muted-foreground/70",
          isRtl ? "border-r-[1.5px] border-r-border/30 border-l-0 pr-3 pl-0 mr-auto ml-0" : "border-l-border/30",
        )}>
          {hasMultipleCategories && (
            <div className="flex flex-wrap gap-0.5 mb-1">
              {segmentCategories.map(cat => {
                const b = CATEGORY_BADGES[cat];
                return b ? <Badge key={cat} variant="outline" className={cn("text-[9px] h-3.5 px-1 border-muted-foreground/20 text-muted-foreground/60", b.className)}>{b.label}</Badge> : null;
              })}
            </div>
          )}
          {(() => {
            const match = msg.content?.match(/^\[ACTION:\s*([^\]]+)\]/);
            if (match) {
              const label = match[1];
              const rest = (msg.content || "").slice(match[0].length).replace(/\s*\{\{ACTION_META:[^}]+\}\}/g, "").trim();
              return (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary">
                    <Swords className="w-3 h-3" />
                    {label}
                  </span>
                  {rest && <span className="text-sm text-foreground/80">{rest}</span>}
                </div>
              );
            }
            return <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>;
          })()}
        </div>
      </div>
    );
  }

  // ── System / OOC / GM Query / Introspection ──
  if (isOOC || isGmQuery || isSystemNote || msgCategory === "system" || msgCategory === "ooc" || msgCategory === "gm_query" || msgCategory === "introspection") {
    return (
      <div className={cn("group/msg relative my-3", isHidden && "opacity-40")}>
        <div className="absolute top-0 right-0 z-10 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-0.5">
          {canDebug && <AgentDebugDialog metadata={msg.metadata} />}
          <MessageCardMenu
            msgId={msg.id} msgContent={msg.content} msgCategory={msgCategory}
            canTTS={false} isThisMsgPlaying={false}
            onSpeak={() => {}} onDownload={() => {}}
            onToggleHide={onToggleHide} isHidden={isHidden}
            onReclassify={onReclassify} onDelete={onDelete}
            onEdit={canEdit ? () => onEdit?.(msg.id, msg.content) : undefined}
            canEdit={canEdit}
            onRetry={onRetry ? () => onRetry(msg.id) : undefined}
            creditsUsed={creditsUsed}
            canReport={canReport}
            worldId={worldId ?? null}
            onDebugVisualize={canDebug ? () => setDebugOpen(true) : undefined}
            onDebugDownload={canDebug ? () => downloadMessageDebugLog(msg) : undefined}
          />
        </div>
        {canDebug && <MessageDebugDialog msg={msg} open={debugOpen} onOpenChange={setDebugOpen} />}
        <div className="text-[10px] text-muted-foreground/50 py-1 border-l-[1.5px] border-l-border/30 pl-2">
          {msg.content.length > 120 ? `${msg.content.slice(0, 120)}…` : msg.content}
        </div>
      </div>
    );
  }

  // ── Narrator prose ──
  return (
    <div dir={isRtl ? "rtl" : undefined} className={cn("group/msg relative my-4", isHidden && "opacity-40")}>
      {/* Menu — top-right, visible on hover */}
      <div className="absolute top-0 right-0 z-10 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-0.5">
        {canDebug && <AgentDebugDialog metadata={msg.metadata} />}
        <MessageCardMenu
          msgId={msg.id} msgContent={msg.content} msgCategory={msgCategory}
          canTTS={true} isThisMsgPlaying={isThisMsgPlaying} isPaused={isPaused}
          onSpeak={speakFull} onDownload={downloadFull}
          onPause={onPause} onResume={onResume} onStop={onStop}
          onToggleHide={onToggleHide} isHidden={isHidden}
          onReclassify={onReclassify} onDelete={onDelete}
          onEdit={canEdit ? () => onEdit?.(msg.id, msg.content) : undefined}
          canEdit={canEdit}
          onRetry={onRetry ? () => onRetry(msg.id) : undefined}
          creditsUsed={creditsUsed}
          canReport={canReport}
          worldId={worldId ?? null}
          onDebugVisualize={canDebug ? () => setDebugOpen(true) : undefined}
          onDebugDownload={canDebug ? () => downloadMessageDebugLog(msg) : undefined}
        />
      </div>
      {canDebug && <MessageDebugDialog msg={msg} open={debugOpen} onOpenChange={setDebugOpen} />}

      {/* Story text */}
      <div className={cn(
        "prose prose-sm dark:prose-invert max-w-none",
        proseClasses,
        isPreviousNarrator ? "mt-0" : "mt-3",
        isNextNarrator ? "mb-0" : "mb-3",
      )}>
        <div className="not-prose mb-3">
          <GMAvatar metadata={msg.metadata} size="sm" />
        </div>
        {hasMultipleCategories && (
          <div className="not-prose flex flex-wrap gap-0.5 mb-2">
            {segmentCategories.map(cat => {
              const b = CATEGORY_BADGES[cat];
              return b ? <Badge key={cat} variant="outline" className={cn("text-[9px] h-3.5 px-1 border-muted-foreground/20 text-muted-foreground/60", b.className)}>{b.label}</Badge> : null;
            })}
          </div>
        )}

        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>

        {showExpand && (
          <button
            onClick={() => setExpanded(true)}
            className="not-prose text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
          >
            <ChevronDown className="w-3 h-3" />Read more
          </button>
        )}
      </div>
    </div>
  );
}
